import React, { useEffect, useState } from "react";
import { portfolio, clients, techniques } from "../mock";
import { useReveal, MarqueeBanner } from "../components/Layout";
import { ArrowUpRight, Star, Play } from "lucide-react";
import { Link } from "react-router-dom";

const Home = () => {
  useReveal();
  const [filter, setFilter] = useState("Wszystkie");
  const categories = ["Wszystkie", ...Array.from(new Set(portfolio.map(p => p.category)))];
  const filtered = filter === "Wszystkie" ? portfolio : portfolio.filter(p => p.category === filter);

  const [hover, setHover] = useState(null);

  return (
    <div className="pt-24">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex flex-col justify-between px-6 md:px-10 pt-8 pb-10 max-w-[1800px] mx-auto">
        <div className="flex items-start justify-between">
          <div className="mono text-[11px] tracking-[0.25em] opacity-60">
            <div>[ 01 — REALIZACJE ]</div>
            <div className="mt-1">EST. ŁÓDŹ / PL</div>
          </div>
          <div className="hidden md:block mono text-[11px] tracking-[0.25em] opacity-60 text-right max-w-[220px]">
            <div>Studio animacji filmowej realizujące krótkie formy w technikach cyfrowych oraz tradycyjnych.</div>
          </div>
        </div>

        <div className="my-auto">
          <div className="flex items-center gap-4 mb-6 rise-in" style={{animationDelay: "0.1s"}}>
            <Star size={14} className="text-[#FF4D2E]" />
            <span className="mono text-[11px] tracking-[0.3em] uppercase">Studio animacji · od 2010</span>
          </div>
          <h1 className="display text-[15vw] md:text-[13vw] leading-[0.85] rise-in" style={{animationDelay: "0.2s"}}>
            <span className="block">OŻYWIAMY</span>
            <span className="block">
              <span className="serif italic font-normal">materię</span>
              <span className="text-[#FF4D2E] serif italic">.</span>
            </span>
            <span className="block text-outline">wyobraźnię</span>
          </h1>
          <div className="mt-10 grid md:grid-cols-3 gap-8 rise-in" style={{animationDelay: "0.4s"}}>
            <div className="md:col-start-2 md:col-span-2 max-w-2xl text-lg md:text-xl leading-snug">
              <span className="serif italic">Animusz Studio</span> nadaje życie firmowym brandom i projektom — od pomysłu po postprodukcję. Krótkie formy, teledyski, spoty, wizualizacje i filmy edukacyjne.
            </div>
          </div>
        </div>

        <div className="flex items-end justify-between">
          <div className="flex items-center gap-3 mono text-[11px] tracking-[0.25em] opacity-60">
            <div className="w-8 h-[1px] bg-black/60" />
            <span>PRZEWIŃ W DÓŁ</span>
          </div>
          <div className="mono text-[11px] tracking-[0.25em] opacity-60">{portfolio.length} REALIZACJI</div>
        </div>
      </section>

      {/* MARQUEE STRIP */}
      <MarqueeBanner className="bg-[#0E0E0E] text-[#F2EEE5]">
        <span className="display text-[5vw] leading-none">2D CYFROWA</span>
        <span className="serif italic text-[#FF4D2E] text-[4vw]">*</span>
        <span className="display text-[5vw] leading-none text-outline" style={{WebkitTextStroke: "1.5px #F2EEE5"}}>LALKOWA</span>
        <span className="serif italic text-[#FF4D2E] text-[4vw]">*</span>
        <span className="display text-[5vw] leading-none">WYCINANKA</span>
        <span className="serif italic text-[#FF4D2E] text-[4vw]">*</span>
        <span className="display text-[5vw] leading-none text-outline" style={{WebkitTextStroke: "1.5px #F2EEE5"}}>PLASTELINA</span>
        <span className="serif italic text-[#FF4D2E] text-[4vw]">*</span>
      </MarqueeBanner>

      {/* FILTERS */}
      <section id="realizacje" className="px-6 md:px-10 max-w-[1800px] mx-auto pt-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 reveal">
          <div>
            <div className="mono text-[11px] tracking-[0.3em] opacity-60 mb-4">[ WYBRANE PROJEKTY — 2019 / 2023 ]</div>
            <h2 className="display text-6xl md:text-8xl">Realizacje.</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button key={cat} onClick={() => setFilter(cat)}
                className={`px-4 py-2 border transition-all duration-300 text-sm ${filter === cat ? "bg-[#0E0E0E] text-[#F2EEE5] border-[#0E0E0E]" : "border-black/20 hover:border-[#0E0E0E]"}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Portfolio grid — masonry-esque */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-6 mt-12">
          {filtered.map((p, idx) => {
            const patterns = [
              "md:col-span-4 md:row-span-2 aspect-[16/10]",
              "md:col-span-2 aspect-[3/4]",
              "md:col-span-2 aspect-square",
              "md:col-span-3 aspect-[4/3]",
              "md:col-span-3 aspect-[4/3]",
              "md:col-span-2 aspect-square",
              "md:col-span-4 aspect-[16/9]",
            ];
            const layout = patterns[idx % patterns.length];
            return (
              <div key={p.id} className={`pf-card reveal group relative overflow-hidden bg-[#E8E2D3] ${layout}`}
                onMouseEnter={() => setHover(p.id)} onMouseLeave={() => setHover(null)}
                style={{transitionDelay: `${(idx % 6) * 60}ms`}}>
                <img src={p.gif} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E0E]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute top-4 left-4 mono text-[10px] tracking-[0.25em] text-[#F2EEE5] bg-[#0E0E0E]/70 backdrop-blur px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {String(idx + 1).padStart(2, "0")} / {p.year}
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-[#F2EEE5] translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <div>
                    <div className="mono text-[10px] tracking-[0.25em] opacity-80">{p.category.toUpperCase()}</div>
                    <div className="serif italic text-2xl md:text-3xl font-bold leading-tight">{p.title}</div>
                    <div className="flex gap-2 mt-2">
                      {p.tags.map(t => <span key={t} className="mono text-[9px] tracking-widest border border-white/40 px-2 py-0.5">{t}</span>)}
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#FF4D2E] flex items-center justify-center flex-shrink-0">
                    <ArrowUpRight size={20} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* TECHNIQUES SECTION */}
      <section className="px-6 md:px-10 max-w-[1800px] mx-auto pt-32">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-16 reveal">
          <div className="md:col-span-4">
            <div className="mono text-[11px] tracking-[0.3em] opacity-60 mb-3">[ TECHNIKI ]</div>
            <h2 className="display text-5xl md:text-7xl">Rzemiosło.</h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 text-lg leading-snug">
            Chcemy reanimować szlachetną i piękną technikę animacji poklatkowej — łącząc cyfrowe narzędzia z ręczną wrażliwością.
          </div>
        </div>

        <div className="border-t border-black/20">
          {techniques.map((t) => (
            <div key={t.id} className="reveal group border-b border-black/20 py-8 md:py-10 grid md:grid-cols-12 gap-6 items-center hover:bg-[#0E0E0E] hover:text-[#F2EEE5] transition-colors duration-500 px-2 md:px-4 -mx-2 md:-mx-4">
              <div className="md:col-span-1 mono text-sm opacity-60">{t.id}</div>
              <div className="md:col-span-5 display text-3xl md:text-5xl">
                <span className="group-hover:italic transition-all duration-500">{t.name}</span>
              </div>
              <div className="md:col-span-5 text-base md:text-lg opacity-80">{t.desc}</div>
              <div className="md:col-span-1 flex justify-end">
                <div className="w-10 h-10 border border-current rounded-full flex items-center justify-center group-hover:bg-[#FF4D2E] group-hover:border-[#FF4D2E] transition-all duration-500">
                  <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-500" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CLIENTS */}
      <section className="px-6 md:px-10 max-w-[1800px] mx-auto pt-32">
        <div className="reveal">
          <div className="mono text-[11px] tracking-[0.3em] opacity-60 mb-4">[ ZAUFALI NAM ]</div>
          <h2 className="display text-4xl md:text-6xl mb-12">Klienci &amp; współprace.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-0 border-t border-l border-black/20">
          {clients.map((c, i) => (
            <div key={c} className="reveal border-r border-b border-black/20 p-8 md:p-10 flex items-center justify-center serif italic text-2xl md:text-3xl hover:bg-[#FF4D2E] hover:text-[#F2EEE5] transition-colors duration-500" style={{transitionDelay: `${i * 40}ms`}}>
              {c}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-10 max-w-[1800px] mx-auto pt-32">
        <div className="bg-[#0E0E0E] text-[#F2EEE5] p-10 md:p-20 reveal">
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <div className="mono text-[11px] tracking-[0.3em] opacity-60 mb-6">[ ZACZNIJMY WSPÓŁPRACĘ ]</div>
              <h3 className="display text-5xl md:text-8xl leading-[0.9]">
                Masz pomysł?<br />
                <span className="serif italic text-[#FF4D2E]">Ożywmy</span> go razem.
              </h3>
            </div>
            <div className="md:col-span-4">
              <Link to="/kontakt" className="group inline-flex items-center gap-3 border border-[#F2EEE5] px-6 py-4 hover:bg-[#FF4D2E] hover:border-[#FF4D2E] transition-colors duration-300">
                <span className="text-lg">Napisz do nas</span>
                <ArrowUpRight size={20} className="group-hover:rotate-45 transition-transform duration-500" />
              </Link>
              <div className="mt-6 mono text-[11px] opacity-60">animuszstudio@gmail.com<br />+48 796 633 631</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
